'use client'

import { useAuthorization } from '@entities/authentication'
import { tutorExclusionMutations, tutorExclusionQueries } from '@entities/tutor-exclusion'
import { track } from '@shared/analytics'
import { useTranslation } from '@shared/config/i18n'
import { EVENT_IDS } from '@shared/constants'
import { useToast } from '@shared/hooks'
import { useMutation, useQueryClient } from '@tanstack/react-query'

interface UseBlockTutorOptions {
  location?: string
}

const trackBlockEvent: typeof track = (eventName, properties) => {
  try {
    track(eventName, properties)
  } catch {
    // Analytics must not change the saved block result or prevent cache invalidation.
  }
}

export const useBlockTutor = ({ location = 'unknown' }: UseBlockTutorOptions = {}) => {
  const queryClient = useQueryClient()
  const { bearerToken } = useAuthorization()
  const { toast } = useToast()
  const t = useTranslation()

  return useMutation({
    ...tutorExclusionMutations.blockTutor({ bearerToken }),
    onSuccess: (_, variables) => {
      toast({ title: t('tutorExclusion.toast.blockSuccess') })
      trackBlockEvent(EVENT_IDS.TUTOR_BLOCKED, { tutorId: variables.tutorId, classId: variables.classId, location })
    },
    onError: (error, variables) => {
      toast({ title: t('tutorExclusion.toast.blockFail') })
      trackBlockEvent(EVENT_IDS.TUTOR_BLOCK_FAILED, {
        tutorId: variables.tutorId,
        error: error instanceof Error ? error.message : String(error),
        location,
      })
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: tutorExclusionQueries.all })
    },
  })
}
